// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React from 'react';
import type {
  FactFindOption,
  FactFindQuestion,
  FactFindSchema,
  ProfileFormVersion,
  RiskResult,
} from '../../api/profileFormApi';

/**
 * Advisor read-only view of a client's Risk Assessment (RISK_ATTITUDE) result.
 *
 * This is a faithful copy of the Client UI result screen — `ReadoutView` in
 * apps/client-ui/src/components/factfind/RiskAssessmentSection.tsx — so the
 * advisor sees the SAME information the client sees: "Your Score" → the 1–7
 * colour scale (result boxed) → the three derived stat cards. Every figure is
 * derived on the frontend from `result` + `schema` + `answers` (the backend
 * only stores { level, raw, flags }); the copy/colours below are pure
 * presentation. The ONLY difference from the Client UI is that the advisor is
 * view-only, so the "Re-Take Assessment" button is intentionally omitted.
 *
 * Keep the constants, helpers and `data-testid`s in sync with the Client UI
 * source of truth (parity is asserted in RiskAssessmentReadout.test.tsx).
 */

// Lilac panel for the result read-out (mobile-first p-24 → lg 40px/240px).
const LILAC_PANEL =
  'self-stretch rounded-card bg-lilac-06 flex flex-col items-center gap-xl p-lg lg:px-[240px] lg:py-xxl';

// ── Result-view presentation constants (editable copy — NOT business logic) ──
const RISK_COLORS: Record<number, string> = {
  1: '#44CE1B',
  2: '#88D32D',
  3: '#BBDB44',
  4: '#F7E379',
  5: '#F2A134',
  6: '#EF7726',
  7: '#EB4443',
};

const LEVEL_META: Record<number, { label: string; subtitle: string }> = {
  1: { label: 'Very Conservative', subtitle: 'Focused on protecting capital and avoiding losses.' },
  2: { label: 'Conservative', subtitle: 'Prefers stability with only modest fluctuation.' },
  3: { label: 'Cautious', subtitle: 'Accepts small fluctuation for modest growth.' },
  4: { label: 'Balanced', subtitle: 'Accepts moderate fluctuation in pursuit of balanced growth.' },
  5: { label: 'Moderate Growth', subtitle: 'Growth-oriented and able to ride out meaningful fluctuation over time.' },
  6: { label: 'Growth', subtitle: 'Strongly growth-oriented; comfortable with significant fluctuation.' },
  7: { label: 'Aggressive Growth', subtitle: 'Maximises growth and accepts large fluctuation.' },
};

// Indexed by the 0–3 financial-literacy sub-score.
const LITERACY_LABELS = ['Very Limited', 'Limited', 'Moderate', 'Strong'];

// Keyed by the `time_horizon` answer value; `primary` is the big figure, `unit`
// the smaller trailing word, `risk` the descriptor line.
const TIME_HORIZON_META: Record<string, { primary: string; unit: string; risk: string }> = {
  lt3: { primary: '<3', unit: 'Years', risk: 'Short-caps Risk' },
  y3_5: { primary: '3-5', unit: 'Years', risk: 'Caps Risk' },
  y6_10: { primary: '6-10', unit: 'Years', risk: 'Moderate Risk' },
  y11_20: { primary: '11-20', unit: 'Years', risk: 'Extends Risk' },
  gt20: { primary: '20+', unit: 'Years', risk: 'Long Horizon' },
};

/** Object-shaped (scored) options for a question, in declaration order. */
function scoredOptions(q: FactFindQuestion): FactFindOption[] {
  return (q.options ?? []).filter((o): o is FactFindOption => typeof o === 'object');
}

/** Max attainable raw score = Σ over questions of the highest option points (28 for the default). */
function maxRawFor(schema: FactFindSchema): number {
  return (schema.questions ?? []).reduce(
    (sum, q) => sum + scoredOptions(q).reduce((m, o) => Math.max(m, o.points ?? 0), 0),
    0,
  );
}

/** The scorer's pre-round value (raw 19 → 4.5); trailing ".0" is dropped via Number(). */
function scaledFrom(raw: number): number {
  return Number((1 + (6 * (raw - 6)) / 22).toFixed(1));
}

/** Financial-literacy sub-score: Σ points over the answered `literacy*` questions, and their max. */
function literacyOf(
  schema: FactFindSchema,
  answers: Record<string, unknown>,
): { score: number; max: number } {
  let score = 0;
  let max = 0;
  for (const q of (schema.questions ?? []).filter((qq) => qq.id.startsWith('literacy'))) {
    const opts = scoredOptions(q);
    max += opts.reduce((m, o) => Math.max(m, o.points ?? 0), 0);
    score += opts.find((o) => o.value === String(answers[q.id]))?.points ?? 0;
  }
  return { score, max };
}

/** Time-horizon display from the `time_horizon` answer (falls back to the option label). */
function timeHorizonOf(
  schema: FactFindSchema,
  answers: Record<string, unknown>,
): { primary: string; unit: string; risk: string } {
  const q = (schema.questions ?? []).find((qq) => qq.id === 'time_horizon');
  const value = q ? answers.time_horizon : undefined;
  if (value != null && TIME_HORIZON_META[String(value)]) return TIME_HORIZON_META[String(value)];
  if (q && value != null) {
    const opt = scoredOptions(q).find((o) => o.value === String(value));
    if (opt) return { primary: opt.label ?? opt.value, unit: '', risk: '—' };
  }
  return { primary: '—', unit: '', risk: '—' };
}

/** Small 4×4 dot used as a separator in a stat card's sub-line. */
const DotDivider: React.FC = () => (
  <svg width="4" height="4" viewBox="0 0 4 4" fill="none" aria-hidden="true" className="shrink-0">
    <circle cx="2" cy="2" r="2" fill="#2D0A6C" />
  </svg>
);

/** One white stat card: title, a big `primary`+small `secondary` figure, and a dot-joined sub-line. */
function StatCard({
  testid,
  title,
  primary,
  secondary,
  sub,
}: {
  testid: string;
  title: string;
  primary: string;
  secondary: string;
  sub: string[];
}) {
  return (
    <div
      data-testid={testid}
      className="flex-1 flex flex-col items-start gap-sm p-md rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)]"
    >
      <span className="text-purple-primary font-sans text-body-03 font-medium leading-normal">
        {title}
      </span>
      <div className="flex items-baseline">
        <span className="text-purple-primary font-sans text-h3 font-normal leading-normal">
          {primary}
        </span>
        <span className="whitespace-pre text-purple-primary font-sans text-h5 font-normal leading-normal">
          {secondary}
        </span>
      </div>
      <div className="flex items-center gap-sm">
        {sub.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <DotDivider />}
            <span className="text-purple-primary font-sans text-body-04 font-normal leading-normal">
              {s}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Read-only result view of the latest submitted version. "Your Score" → level
 * label + subtitle → a 1–7 colour scale with the result boxed → three derived
 * stat cards. No "Re-Take" — the advisor can only view.
 */
export function RiskAssessmentReadout({
  schema,
  latest,
}: {
  schema: FactFindSchema;
  latest: ProfileFormVersion;
}) {
  const riskResult = latest.result as RiskResult | null | undefined;
  const level = Math.min(Math.max(riskResult?.level ?? 1, 1), 7);
  const raw = riskResult?.raw ?? 0;
  const meta = LEVEL_META[level];
  const maxRaw = maxRawFor(schema);
  const lit = literacyOf(schema, latest.answers);
  const horizon = timeHorizonOf(schema, latest.answers);
  const litLabel = LITERACY_LABELS[Math.min(lit.score, LITERACY_LABELS.length - 1)];

  return (
    <div className={LILAC_PANEL}>
      <div data-testid="risk-assessment-readout" className="flex flex-col items-center gap-md self-stretch">
        <div className="flex flex-col items-center gap-sm self-stretch">
          <p className="text-black text-center font-sans text-body-04 font-medium leading-normal m-0">
            Your Score
          </p>
          <h2
            data-testid="risk-score-title"
            className="text-black text-center font-sans text-body-01 font-medium leading-normal m-0"
          >
            {meta.label}
          </h2>
          <p
            data-testid="risk-score-subtitle"
            className="text-black text-center font-sans text-body-03 font-normal leading-normal m-0"
          >
            {meta.subtitle}
          </p>
        </div>

        {/* Low → High risk colour scale, with the result level boxed. */}
        <div
          data-testid="risk-scale"
          className="flex w-full max-w-[500px] items-center justify-center gap-sm p-md rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] overflow-x-auto"
        >
          <span className="shrink-0 mr-sm text-[#000] font-sans text-body-04 font-normal leading-normal">
            Low Risk
          </span>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => {
            const square = (
              <div
                className="w-[24px] h-[24px] shrink-0 flex flex-col justify-center items-center rounded-[6px] text-[#000] font-sans text-body-03 font-normal leading-normal"
                style={{ backgroundColor: RISK_COLORS[n] }}
              >
                {n}
              </div>
            );
            return n === level ? (
              <div
                key={n}
                data-testid={`risk-scale-item-${n}`}
                aria-current="true"
                className="w-[40px] h-[40px] shrink-0 flex justify-center items-center rounded-lg border border-purple-primary bg-lilac-05"
              >
                {square}
              </div>
            ) : (
              <div key={n} data-testid={`risk-scale-item-${n}`} className="shrink-0">
                {square}
              </div>
            );
          })}
          <span className="shrink-0 ml-sm text-[#000] font-sans text-body-04 font-normal leading-normal">
            High Risk
          </span>
        </div>

        <div className="flex flex-col md:flex-row items-stretch justify-center gap-sm self-stretch">
          <StatCard
            testid="risk-stat-appropriate"
            title="Appropriate risk level"
            primary={`${level}/`}
            secondary="7"
            sub={[`Raw ${raw}/${maxRaw}`, `Scaled ${scaledFrom(raw)}`]}
          />
          <StatCard
            testid="risk-stat-literacy"
            title="Financial Literacy"
            primary={`${lit.score}/`}
            secondary={`${lit.max}`}
            sub={[litLabel]}
          />
          <StatCard
            testid="risk-stat-horizon"
            title="Time Horizon"
            primary={horizon.primary}
            secondary={horizon.unit ? '\u00A0' + horizon.unit : ''}
            sub={[horizon.risk]}
          />
        </div>
      </div>
    </div>
  );
}

export default RiskAssessmentReadout;
